"use client";

import { PortalBookingCancel } from "./PortalBookingCancel";
import type { ReactNode } from "react";
import { hasEventTickets } from "../lib/eventParticipation";
import { usePortalTranslations } from "../i18n/client";
import { usePortalPreferences } from "./PortalPreferences";
import type { PortalTranslationKey } from "../i18n/messages";
import type { CoachArea, PortalSubscription, ScheduleItem } from "../lib/clientPortalApi";
import styles from "./PortalPersonalViews.module.css";

function formatDate(value: string, timeZone = "Europe/Prague", locale = "cs-CZ") {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat(locale, {
    day: "numeric", month: "long", year: "numeric",
    timeZone: /^\d{4}-\d{2}-\d{2}$/.test(value) ? "UTC" : timeZone,
  }).format(date) : "Termín není uveden";
}

function formatTime(value: string, timeZone = "Europe/Prague", locale = "cs-CZ") {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat(locale, {
    hour: "2-digit", minute: "2-digit", timeZone,
  }).format(date) : "—";
}

function clampProgress(value: number) {
  return Number.isFinite(value) ? Math.round(Math.min(100, Math.max(0, value))) : 0;
}

function Icon({ name }: { name: "target" | "check" | "clock" | "calendar" }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {name === "target" ? <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><path d="m12 12 9-9M16 3h5v5" /></> : null}
      {name === "check" ? <><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></> : null}
      {name === "clock" ? <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></> : null}
      {name === "calendar" ? <><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M8 3v4m8-4v4M4 11h16" /></> : null}
    </svg>
  );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return <header className={styles.sectionHeading}><h3>{title}</h3><p>{description}</p></header>;
}

function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return <div className={styles.emptyState}><strong>{title}</strong>{children ? <p>{children}</p> : null}</div>;
}

function Progress({ value, label, completed = false }: { value: number; label: string; completed?: boolean }) {
  const progress = clampProgress(value);
  return (
    <div className={styles.progressGroup}>
      <div
        className={`${styles.progressTrack} ${completed ? styles.progressComplete : ""}`}
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        aria-valuetext={`${progress} %`}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
      <span className={styles.progressNumber} aria-hidden="true">{progress} %</span>
    </div>
  );
}

const collaborationStatuses: Record<string, string> = {
  active: "Aktivní spolupráce",
  paused: "Pozastavená spolupráce",
  completed: "Dokončená spolupráce",
  archived: "Archivovaná spolupráce",
};
const goalStatuses: Record<string, string> = {
  active: "Probíhá",
  paused: "Pozastaveno",
  achieved: "Splněno",
  archived: "Archivováno",
};
const bookingStatuses: Record<string, PortalTranslationKey> = {
  pending: "schedule.status.pending",
  confirmed: "schedule.status.confirmed",
  cancelled: "schedule.status.cancelled",
  completed: "schedule.status.completed",
  no_show: "schedule.status.no_show",
};

export function CoachDashboard({ profiles, reservationHref }: { profiles: CoachArea[]; reservationHref?: string }) {
  if (profiles.length === 0) {
    return <EmptyState title="Spolupráce zatím nezačala">Jakmile vám kouč nastaví spolupráci, uvidíte zde svůj program, cíle a úkoly.</EmptyState>;
  }

  return (
    <div className={styles.profiles}>
      {profiles.map((profile) => (
        <article className={styles.profile} key={profile.clientUuid}>
          <header className={styles.profileHeading}>
            {profiles.length > 1 ? <h2>{profile.profileName}</h2> : null}
            <span className={`${styles.badge} ${profile.status === "active" ? styles.successBadge : styles.mutedBadge}`}>
              {collaborationStatuses[profile.status] ?? "Stav spolupráce není uveden"}
            </span>
          </header>

          <section className={styles.section}>
            <SectionHeading title="Můj program" description="Aktuální plán a váš společný pokrok." />
            <div className={styles.programCard}>
              <div className={styles.programCopy}>
                <h4>{profile.program?.name ?? "Program zatím není přiřazen"}</h4>
                {profile.goalSummary ? <p>{profile.goalSummary}</p> : !profile.program ? <p>Váš kouč zde může připravit plán spolupráce.</p> : null}
              </div>
              {profile.program ? (
                <div className={styles.programProgress}>
                  <span>Krok {profile.program.currentUnit}{profile.program.totalUnits !== null ? ` z ${profile.program.totalUnits}` : ""}</span>
                  <strong aria-hidden="true">{clampProgress(profile.program.progress)} %</strong>
                  <Progress value={profile.program.progress} label={`Pokrok programu ${profile.program.name}`} />
                </div>
              ) : null}
            </div>
          </section>

          <section className={styles.section}>
            <SectionHeading title="Moje cíle" description="Vaše cíle a jejich aktuální naplnění." />
            {profile.goals.length === 0 ? <EmptyState title="Zatím bez cílů">Cíle nastavené vaším koučem se zobrazí tady.</EmptyState> : (
              <ul className={styles.listCard}>
                {profile.goals.map((goal) => (
                  <li className={styles.goalRow} key={goal.uuid}>
                    <span className={`${styles.icon} ${goal.status === "achieved" ? styles.successIcon : ""}`}><Icon name="target" /></span>
                    <div className={styles.rowCopy}><h4>{goal.title}</h4>{goal.dueAt ? <p>Do {formatDate(goal.dueAt)}</p> : null}</div>
                    <Progress value={goal.progress} label={`Naplnění cíle ${goal.title}`} completed={goal.status === "achieved"} />
                    <span className={`${styles.badge} ${goal.status === "achieved" ? styles.successBadge : styles.mutedBadge}`}>{goalStatuses[goal.status] ?? "Stav není uveden"}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.section}>
            <SectionHeading title="Úkoly od kouče" description="Úkoly zadané v rámci vaší spolupráce." />
            {profile.tasks.length === 0 ? <EmptyState title="Zatím bez úkolů">Jakmile vám kouč zadá úkol, najdete ho tady.</EmptyState> : (
              <ul className={styles.listCard}>
                {profile.tasks.map((task) => (
                  <li className={styles.taskRow} key={task.uuid}>
                    <span className={`${styles.icon} ${task.completed ? styles.successIcon : ""}`}><Icon name={task.completed ? "check" : "clock"} /></span>
                    <div className={styles.rowCopy}><h4>{task.title}</h4>{task.dueAt ? <p>Termín: {formatDate(task.dueAt)}</p> : null}</div>
                    <span className={`${styles.badge} ${task.completed ? styles.successBadge : styles.pendingBadge}`}>{task.completed ? "Dokončeno" : "Čeká na splnění"}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.section}>
            <SectionHeading title="Moje setkání" description="Přehled společných termínů a konzultací." />
            <ScheduleList items={profile.sessions} />
            {reservationHref ? <a className={styles.textLink} href={reservationHref}>Rezervovat <span aria-hidden="true">→</span></a> : null}
          </section>
        </article>
      ))}
    </div>
  );
}

export function ScheduleList({ items, compact = false, plain = false, portalSlug }: { items: ScheduleItem[]; compact?: boolean; plain?: boolean; portalSlug?: string }) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  if (items.length === 0) return <EmptyState title={t("schedule.emptyTitle")}>{t("schedule.emptyDescription")}</EmptyState>;

  return (
    <ul className={`${styles.listCard} ${compact ? styles.compactList : ""} ${plain ? styles.plainList : ""}`}>
      {items.map((item) => (
        <li className={styles.scheduleRow} key={item.uuid}>
          <span className={styles.icon}><Icon name="calendar" /></span>
          <div className={styles.rowCopy}><h4>{item.serviceName}</h4>{item.location ? <p>{item.location}</p> : null}{hasEventTickets(item) ? <p>{t("tickets.quantity", {count: item.capacityUnits ?? 1})}</p> : null}</div>
          <div className={styles.scheduleDate}>
            {formatDate(item.startAt, item.timezone, locale) === formatDate(item.endAt, item.timezone, locale) ? <>
              <time dateTime={item.startAt}>{formatDate(item.startAt, item.timezone, locale)}</time>
              <span>{formatTime(item.startAt, item.timezone, locale)} – {formatTime(item.endAt, item.timezone, locale)}</span>
            </> : <>
              <time dateTime={item.startAt}>{formatDate(item.startAt, item.timezone, locale)} {formatTime(item.startAt, item.timezone, locale)}</time>
              <time dateTime={item.endAt}>– {formatDate(item.endAt, item.timezone, locale)} {formatTime(item.endAt, item.timezone, locale)}</time>
            </>}
          </div>
          {item.resourceName ? <span className={styles.resourceName}>{item.resourceName}</span> : <span />}
          <span className={`${styles.badge} ${item.status === "confirmed" || item.status === "completed" ? styles.successBadge : item.status === "pending" ? styles.pendingBadge : styles.mutedBadge}`}>{t(bookingStatuses[item.status] ?? "schedule.status.unknown")}</span>
          {portalSlug && item.canCancel && new Date(item.startAt).getTime() > Date.now() ? <PortalBookingCancel slug={portalSlug} bookingUuid={item.uuid} /> : null}
        </li>
      ))}
    </ul>
  );
}

function formatSubscriptionAmount(subscription: PortalSubscription) {
  if (subscription.amount === null) return "Cena není uvedena";
  const value = Number(subscription.amount);
  if (!Number.isFinite(value)) return `${subscription.amount} ${subscription.currency}`;

  try {
    return new Intl.NumberFormat("cs-CZ", { style: "currency", currency: subscription.currency, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${subscription.amount} ${subscription.currency}`;
  }
}

export function SubscriptionDashboard({ subscriptions }: { subscriptions: PortalSubscription[] }) {
  if (subscriptions.length === 0) {
    return <EmptyState title="Zatím nemáte předplatné">Jakmile vám správce přiřadí plán, jeho stav a další obnovení uvidíte tady.</EmptyState>;
  }

  return (
    <section className={styles.subscriptions} aria-label="Moje předplatné">
      <p className={styles.intro}>Přehled přiřazených plánů a jejich aktuálního stavu.</p>
      {subscriptions.map((subscription) => (
        <article className={styles.subscriptionCard} key={subscription.uuid}>
          <div className={styles.subscriptionTop}>
            <div>
              <h2>{subscription.planName}</h2>
              {subscription.groupName !== subscription.planName ? <p>{subscription.groupName}</p> : null}
              <p>{subscription.profileName}</p>
            </div>
            <span className={`${styles.badge} ${subscription.active ? styles.successBadge : styles.mutedBadge}`}>{subscription.active ? "Aktivní" : "Neaktivní"}</span>
          </div>
          <dl className={styles.subscriptionDetails}>
            <div><dt>Cena</dt><dd>{formatSubscriptionAmount(subscription)}</dd></div>
            <div><dt>Fakturace</dt><dd>{subscription.billingPeriod === "monthly" ? "Měsíčně" : "Ročně"}</dd></div>
            <div><dt>Další obnovení</dt><dd><time dateTime={subscription.nextRenewalAt}>{formatDate(subscription.nextRenewalAt)}</time></dd></div>
          </dl>
        </article>
      ))}
    </section>
  );
}
